import { isNil, isNotNil } from "@utils/assert";
import { TRUE_STRING } from "@utils/boolean-utils";

export function capitalizeFirstLetter(text: string): string {
	const [first, ...rest] = text;
	return [first.toUpperCase(), ...rest].join("");
}

export function removeDiacriticsFromString(text: string): string {
	return (
		text
			.toLowerCase()
			// separate special caracters from their original character (ex: ñ => n~)
			.normalize("NFD")
			// remove special characters (ex: n~ => n)
			.replace(/[\u0300-\u036f]/g, "")
	);
}

export const findOccurrences = (
	text: string,
	substring: string,
	options: { caseInsensitive: boolean; diacriticsInsensitive: boolean } = {
		caseInsensitive: true,
		diacriticsInsensitive: true,
	},
): number[] => {
	let processedText = text;
	let processedSubstring = substring;
	if (options.caseInsensitive) {
		processedText = processedText.toLowerCase();
		processedSubstring = processedSubstring.toLowerCase();
	}
	if (options.diacriticsInsensitive) {
		processedText = removeDiacriticsFromString(processedText);
		processedSubstring = removeDiacriticsFromString(processedSubstring);
	}
	const result: number[] = [];
	for (let index = 0; index <= text.length - substring.length; index++) {
		if (processedText.slice(index).startsWith(processedSubstring)) {
			result.push(index);
		}
	}
	return result;
};

export const splitTextWithQueryString = (
	text: string,
	queryString: string,
): {
	text: string;
	isQueryString: boolean;
}[] => {
	let currentIndex = 0;
	const result: {
		text: string;
		isQueryString: boolean;
	}[] = [];
	findOccurrences(text, queryString).forEach(occurrenceIndex => {
		if (currentIndex !== occurrenceIndex) {
			result.push({ isQueryString: false, text: text.slice(currentIndex, occurrenceIndex) });
			currentIndex = occurrenceIndex;
		}
		result.push({ isQueryString: true, text: text.slice(occurrenceIndex, occurrenceIndex + queryString.length) });
		currentIndex = occurrenceIndex + queryString.length;
	});
	if (currentIndex !== text.length) {
		result.push({ isQueryString: false, text: text.slice(currentIndex) });
	}
	return result;
};

interface Names {
	firstName?: string;
	lastName?: string;
}

export const formatName = ({ firstName, lastName }: Names, defaultName = ""): string => {
	if (isNotNil(firstName) || isNotNil(lastName)) {
		return [firstName, lastName].join(" ");
	} else {
		return defaultName;
	}
};

export const stringToBoolean = (value: string): boolean => value.toLowerCase() === TRUE_STRING;
export const isNumeric = (value: string): boolean => !isNaN(+value);

export const truncateString = (value: string, n: number): string =>
	value.length > n ? value.slice(0, n - 1) + "..." : value;

export const isWordMatchingSearch = (word: string, search: string): boolean =>
	removeDiacriticsFromString(word).includes(removeDiacriticsFromString(search));

export const orderList: { (list: string[]): string[]; <T>(list: T[], property: keyof T): T[] } = <
	T extends { [key: string]: string },
>(
	list: T[] | string[],
	property?: keyof T,
): T[] | string[] => {
	const collator = new Intl.Collator();
	return isNil(property)
		? (list as string[]).sort((a, b) => collator.compare(a, b))
		: (list as T[]).sort((a, b) => collator.compare(a[property], b[property]));
};

export const getInitials = (name: string): string =>
	name
		.trim()
		.split(/\s+/)
		.map(word => word[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();

export const formatTitle = (title: string | undefined, maxLength = 63): string => {
	if (!title) return "";
	if (title.length <= maxLength) return title;
	return `${title.slice(0, maxLength - 3).trimEnd()}...`;
};
